import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useMsal } from '@azure/msal-react';
import { useToast } from '@/context/ToastContext';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import FormNavigationLink from '@/common/FormNavigationLink';
import ConnectivityIndicator from '@/common/ConnectivityIndicator';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { UserSessionHelper, WebViewHelper } from '@/library';
import { IMenuSettings } from '@/library/MenuSettingsHelper';
import { appVersion, isIOSWebView, isLocalEnv } from '@/utils';
import titleistLogoDev from '@/assets/images/titleist-logo-dev.png';
import titleistLogoUat from '@/assets/images/titleist-logo-uat.png';
import titleistLogoProd from '@/assets/images/titleist-logo.png';
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react';
import styles from './SidebarMenu.module.scss';

let titleistLogo = titleistLogoProd;
if (import.meta.env.TOURPWA_ENVIRONMENT === 'staging') titleistLogo = titleistLogoDev;
if (import.meta.env.TOURPWA_ENVIRONMENT === 'uat') titleistLogo = titleistLogoUat;

interface IPages {
  page: string;
  label: string;
  // Form for legacy redirect, link for migrated module
  type: 'form' | 'link';
  isVisible: boolean;
  availableOffline: boolean; // update availability as modules are migrated
}

const getPages = (pageSettings: IMenuSettings): IPages[] => {
  return [
    { page: 'tourbag', label: 'Tour Bag', type: 'link' as const, isVisible: pageSettings.tourBag, availableOffline: true },
    { page: 'Order', label: 'Order', type: 'form' as const, isVisible: pageSettings.order, availableOffline: false },
    { page: 'Orders', label: 'Orders (PWA)', type: 'link' as const, isVisible: pageSettings.orders, availableOffline: false },
    { page: 'Shaft', label: 'Shaft', type: 'form' as const, isVisible: pageSettings.shaft, availableOffline: false },
    { page: 'Inventory', label: 'Inventory', type: 'form' as const, isVisible: pageSettings.inventory, availableOffline: false },
    { page: 'RnD', label: 'Master List', type: 'form' as const, isVisible: pageSettings.masterList, availableOffline: false },
    { page: 'Admin', label: 'Admin', type: 'form' as const, isVisible: pageSettings.admin, availableOffline: false },
    { page: 'Wgr', label: 'WGR', type: 'form' as const, isVisible: pageSettings.wgr, availableOffline: false },
  ];
};

interface ISidebarMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

function SidebarMenu({ isOpen, onClose }: ISidebarMenuProps) {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { instance } = useMsal();
  const { showToast } = useToast();
  const { startSync, lastSyncDate, syncStatus } = useBackgroundSync();
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string>('');
  const [pages, setPages] = useState<IPages[]>();

  useEffect(() => {
    const fetchSettings = async () => {
      const token = await UserSessionHelper.getToken();
      setToken(token || '');
      const user = await UserSessionHelper.getUser();
      const menuSettings = user?.menuSettings as IMenuSettings;
      const pages = menuSettings ? getPages(menuSettings) : [];
      setPages(pages);
      setLoading(false);
    };
    fetchSettings();
  }, []);

  const logout = async () => {
    // clear user session and proceed with logout
    await UserSessionHelper.clearSession();
    if (isIOSWebView) {
      try {
        WebViewHelper.iOSLogout();
      } catch (error) {
        console.error('iOS bridge not available, falling back to PWA login flow:', error);
        // TODO: Should we close the WebView or fallback to PWA SSO?
      }
    } else {
      await UserSessionHelper.signOut(instance);
      if (isLocalEnv) {
        navigate('/', { replace: true }); // use replace to avoid back navigation to protected routes
      } else {
        //redirect to the Vue app logout to clear out their user session as well. They redirect to our login page afterwards
        const origin = window.location.origin;
        const url = origin + '/api/Account/GetLogOut';
        window.location.href = url;
      }
    }
  };

  const sync = () => {
    startSync({ forceUpdate: true });
    showToast({ message: 'Syncing player data in the background…' });
  };

  if (loading) return null;

  return (
    <>
      <div className={`${styles.overlay} ${isOpen ? styles.visible : ''}`} onClick={onClose} />
      <aside className={`${styles.sidebarMenu} ${isOpen ? styles.open : ''}`}>
        <div>
          <img src={titleistLogo} alt="Titleist Logo" className={styles.titleistLogo} />
        </div>
        <div className={styles.sidebarContainer}>
          <nav className={styles.sidebarLinks}>
            {pages
              ?.filter((page) => page.isVisible)
              ?.map(({ page, label, type, availableOffline }) => {
                if (!isOnline && !availableOffline) return;
                if (type === 'link') {
                  return (
                    <div key={page} data-testid={`sidebar-link-${page}`}>
                      <Link to={`/${page}`}>
                        {label} <ArrowRightIcon />
                      </Link>
                    </div>
                  );
                } else if (!isIOSWebView) {
                  return <FormNavigationLink key={label} page={page} label={label} token={token} />;
                }
              })}
          </nav>
          {isIOSWebView && <ConnectivityIndicator showLabel={true} className={styles.onlineStatus} />}
        </div>
        <div className={styles.footer}>
          <div className={styles.versionText}>TOUR {appVersion}</div>
          <a href="mailto:Alexander_Allor@acushnetgolf.com?subject=Support">Support</a>

          <button className={styles.sync} onClick={sync}>
            Sync
          </button>
          {syncStatus === 'error' && <div className={styles.syncError}>Sync error</div>}
          {lastSyncDate && syncStatus !== 'error' && <div className={styles.lastSync}>Last sync: {dayjs(lastSyncDate).format('MM/DD HH:mm')}</div>}
          <button className={styles.logOut} onClick={logout}>
            Log Out
          </button>
        </div>
      </aside>
    </>
  );
}

export default SidebarMenu;
